import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import {
  getWebhookEvents,
  WebhookEvent,
  WebhookEventStatus,
} from '../api/smartcar.api';
import { toast } from '../utils/toast';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleString();
}

function truncateId(id: unknown, length = 16): string {
  if (id == null) return '—';
  const s = String(id);
  if (s.length <= length) return s;
  return `${s.slice(0, length)}…`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<WebhookEventStatus, string> = {
  ok:            'bg-green-100 text-green-800',
  no_connection: 'bg-yellow-100 text-yellow-800',
  trip_error:    'bg-red-100 text-red-800',
  sig_invalid:   'bg-red-100 text-red-800',
};

function StatusBadge({ status }: { status: WebhookEventStatus }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {t(`webhookEvents.status.${status}`, status)}
    </span>
  );
}

function TripCreatedCell({ value }: { value: boolean }) {
  if (value) {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  }
  return <span className="text-gray-400">—</span>;
}

interface ExpandedPanelProps {
  event: WebhookEvent;
}

function ExpandedPanel({ event }: ExpandedPanelProps) {
  const { t } = useTranslation();
  return (
    <tr>
      <td colSpan={7} className="px-4 py-0">
        <div className="my-3 border border-gray-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
            {/* Raw Payload */}
            <div className="p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                {t('webhookEvents.rawPayload')}
              </p>
              <pre className="text-xs font-mono text-gray-700 bg-gray-50 rounded p-3 overflow-x-auto max-h-64">
                {event.raw_payload
                  ? JSON.stringify(event.raw_payload, null, 2)
                  : <span className="text-gray-400">null</span>}
              </pre>
            </div>
            {/* Extracted Patch */}
            <div className="p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                {t('webhookEvents.extractedPatch')}
              </p>
              <pre className="text-xs font-mono text-gray-700 bg-gray-50 rounded p-3 overflow-x-auto max-h-64">
                {event.patch
                  ? JSON.stringify(event.patch, null, 2)
                  : <span className="text-gray-400">null</span>}
              </pre>
            </div>
          </div>
          {/* Processing error, if any */}
          {event.processing_error && (
            <div className="px-4 py-3 bg-red-50 border-t border-red-200">
              <p className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-1">
                Processing Error
              </p>
              <p className="text-xs font-mono text-red-700">{event.processing_error}</p>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: '',             labelKey: 'webhookEvents.filterAllStatuses' },
  { value: 'ok',          labelKey: 'webhookEvents.status.ok' },
  { value: 'no_connection', labelKey: 'webhookEvents.status.no_connection' },
  { value: 'trip_error',  labelKey: 'webhookEvents.status.trip_error' },
  { value: 'sig_invalid', labelKey: 'webhookEvents.status.sig_invalid' },
];

const LIMIT = 50;

export const SmartcarWebhookEventsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin === true || user?.role === 'super_admin';

  // Guard: redirect non-super_admin users
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;

  // ---- State ----
  const [events, setEvents]     = useState<WebhookEvent[]>([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [page, setPage]         = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filters
  const [vehicleIdFilter, setVehicleIdFilter] = useState('');
  const [statusFilter, setStatusFilter]       = useState('');
  const [fromFilter, setFromFilter]           = useState('');
  const [toFilter, setToFilter]               = useState('');

  // ---- Data fetching ----
  const fetchEvents = useCallback(async (targetPage: number) => {
    setIsLoading(true);
    try {
      const data = await getWebhookEvents({
        page:       targetPage,
        limit:      LIMIT,
        vehicle_id: vehicleIdFilter.trim() || undefined,
        status:     statusFilter || undefined,
        from:       fromFilter || undefined,
        to:         toFilter   || undefined,
      });
      setEvents(data.events);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load webhook events.');
    } finally {
      setIsLoading(false);
    }
  }, [vehicleIdFilter, statusFilter, fromFilter, toFilter]);

  // Initial load
  useEffect(() => {
    fetchEvents(1);
    setPage(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setPage(1);
    setExpandedId(null);
    fetchEvents(1);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
    setExpandedId(null);
    fetchEvents(p);
  };

  const handleRefresh = () => {
    setExpandedId(null);
    fetchEvents(page);
  };

  const toggleExpanded = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  // ---- Render ----
  return (
    <Layout>
      <div className="p-6 space-y-6">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('webhookEvents.title')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {t('webhookEvents.subtitle')}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <svg
              className="w-4 h-4 mr-1.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {isLoading ? t('common.loading') : t('common.refresh', 'Refresh')}
          </Button>
        </div>

        {/* ── Filters ── */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Vehicle ID search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('webhookEvents.filterVehicleId', 'Smartcar Vehicle ID')}
              </label>
              <input
                type="text"
                value={vehicleIdFilter}
                onChange={e => setVehicleIdFilter(e.target.value)}
                placeholder="e.g. 36ab27d0-fd9d..."
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Status filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('webhookEvents.filterStatus', 'Status')}
              </label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey, opt.value || 'All')}
                  </option>
                ))}
              </select>
            </div>

            {/* From date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('deviceLogs.filterFrom', 'From')}
              </label>
              <input
                type="datetime-local"
                value={fromFilter}
                onChange={e => setFromFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* To date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('deviceLogs.filterTo', 'To')}
              </label>
              <input
                type="datetime-local"
                value={toFilter}
                onChange={e => setToFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button onClick={handleSearch} disabled={isLoading}>
              {t('deviceLogs.search', 'Search')}
            </Button>
          </div>
        </div>

        {/* ── Results count ── */}
        <div className="text-sm text-gray-500">
          {t('deviceLogs.totalResults', { count: total })}
        </div>

        {/* ── Table ── */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('webhookEvents.col.receivedAt', 'Received At')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('webhookEvents.col.vehicle', 'Vehicle')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('webhookEvents.col.eventType', 'Event Type')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('webhookEvents.col.status', 'Status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('webhookEvents.tripCreated')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('webhookEvents.col.tripId', 'Trip ID')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('webhookEvents.col.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                      {t('common.loading')}
                    </td>
                  </tr>
                ) : events.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        {/* Webhook/signal icon */}
                        <svg
                          className="w-10 h-10 text-gray-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
                          />
                        </svg>
                        <p className="text-sm text-gray-500">{t('webhookEvents.noEvents')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  events.map(event => (
                    <React.Fragment key={event._id}>
                      <tr
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                          expandedId === event._id ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => toggleExpanded(event._id)}
                      >
                        {/* Received At */}
                        <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap font-mono">
                          {formatTimestamp(event.received_at)}
                        </td>

                        {/* Smartcar Vehicle ID (truncated) */}
                        <td className="px-4 py-3 text-xs font-mono text-gray-900 whitespace-nowrap">
                          <span title={event.smartcar_vehicle_id ?? undefined}>
                            {truncateId(event.smartcar_vehicle_id)}
                          </span>
                        </td>

                        {/* Event Type */}
                        <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                            {event.event_type}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <StatusBadge status={event.processing_status} />
                        </td>

                        {/* Trip Created */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <TripCreatedCell value={event.trip_created} />
                        </td>

                        {/* Trip ID */}
                        <td className="px-4 py-3 text-xs font-mono text-gray-600 whitespace-nowrap">
                          {event.trip_id ? (
                            <span title={String(event.trip_id)}>{truncateId(event.trip_id, 12)}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        {/* Expand toggle */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
                            onClick={e => { e.stopPropagation(); toggleExpanded(event._id); }}
                            aria-expanded={expandedId === event._id}
                            aria-label={expandedId === event._id ? 'Collapse details' : 'Expand details'}
                          >
                            <svg
                              className={`w-4 h-4 transition-transform ${
                                expandedId === event._id ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            {expandedId === event._id
                              ? t('common.collapse', 'Collapse')
                              : t('common.details', 'Details')}
                          </button>
                        </td>
                      </tr>

                      {/* Inline expanded detail panel */}
                      {expandedId === event._id && (
                        <ExpandedPanel event={event} />
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {pages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {t('deviceLogs.page', { page, pages })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1 || isLoading}
                >
                  {t('common.previous')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= pages || isLoading}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
};
